import { useState, useEffect } from "react";
import {
  Users, Building2, FolderOpen, Search, CheckCircle2, Loader2, UserPlus
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import api from "../../lib/api";
import { toast } from "sonner";

const TAB_CONFIG = [
  { key: "people", label: "People", icon: Users },
  { key: "groups", label: "Groups", icon: FolderOpen },
  { key: "departments", label: "Departments", icon: Building2 },
];

/**
 * Reusable Assignment Modal for assigning learning paths or courses
 * to individual users, groups, or departments.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (v) => void
 *  - title: string (e.g. path name or course name)
 *  - onAssign: (userIds: string[]) => Promise<void>  — called with resolved user IDs
 */
const AssignmentModal = ({ open, onOpenChange, title, onAssign }) => {
  const [tab, setTab] = useState("people");
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  // Selections
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());
  const [selectedDeptNames, setSelectedDeptNames] = useState(new Set());

  useEffect(() => {
    if (open) {
      setSelectedUserIds(new Set());
      setSelectedGroupIds(new Set());
      setSelectedDeptNames(new Set());
      setSearch("");
      setTab("people");
      fetchData();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, groupsRes, deptsRes] = await Promise.all([
        api.get("/users").catch(() => ({ data: [] })),
        api.get("/groups").catch(() => ({ data: [] })),
        api.get("/departments").catch(() => ({ data: [] })),
      ]);
      const u = usersRes.data?.users || usersRes.data || [];
      setUsers(Array.isArray(u) ? u : []);
      const g = groupsRes.data || [];
      setGroups(Array.isArray(g) ? g : []);
      const d = deptsRes.data || [];
      setDepartments(Array.isArray(d) ? d : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const toggleUser = (id) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleGroup = (id) => {
    setSelectedGroupIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleDept = (name) => {
    setSelectedDeptNames(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  // Resolve all selections into a unique list of user IDs
  const resolveUserIds = () => {
    const ids = new Set(selectedUserIds);

    // Add group members
    for (const gId of selectedGroupIds) {
      const group = groups.find(g => g.id === gId);
      if (group?.member_ids) group.member_ids.forEach(id => ids.add(id));
    }

    // Add department members
    for (const dName of selectedDeptNames) {
      users.filter(u => u.department === dName).forEach(u => ids.add(u.id));
    }

    return [...ids];
  };

  const totalSelected = selectedUserIds.size + selectedGroupIds.size + selectedDeptNames.size;

  const handleAssign = async () => {
    const userIds = resolveUserIds();
    if (userIds.length === 0) {
      toast.error("No users resolved from selection");
      return;
    }
    setAssigning(true);
    try {
      await onAssign(userIds);
    } finally { setAssigning(false); }
    onOpenChange(false);
  };

  const filteredUsers = users.filter(u =>
    ["learner", "manager", "trainer"].includes(u.role) &&
    (u.full_name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase()))
  );

  const filteredGroups = groups.filter(g =>
    g.name?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredDepts = departments.filter(d =>
    d.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col" data-testid="assignment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-primary" /> Assign
          </DialogTitle>
          <DialogDescription className="truncate">Assign "{title}" to people, groups, or departments</DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-lg" data-testid="assign-tabs">
          {TAB_CONFIG.map(t => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setSearch(""); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all flex-1 justify-center ${
                tab === t.key ? "bg-white shadow-sm text-primary" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`tab-${t.key}`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
              {t.key === "people" && selectedUserIds.size > 0 && <Badge className="bg-primary/10 text-primary text-[9px] h-4 px-1">{selectedUserIds.size}</Badge>}
              {t.key === "groups" && selectedGroupIds.size > 0 && <Badge className="bg-primary/10 text-primary text-[9px] h-4 px-1">{selectedGroupIds.size}</Badge>}
              {t.key === "departments" && selectedDeptNames.size > 0 && <Badge className="bg-primary/10 text-primary text-[9px] h-4 px-1">{selectedDeptNames.size}</Badge>}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={`Search ${tab}...`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            data-testid="assign-search"
          />
        </div>

        {/* Selection Summary */}
        {totalSelected > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {selectedUserIds.size > 0 && <Badge variant="outline" className="text-[10px]"><Users className="w-2.5 h-2.5 mr-1" />{selectedUserIds.size} people</Badge>}
            {selectedGroupIds.size > 0 && <Badge variant="outline" className="text-[10px]"><FolderOpen className="w-2.5 h-2.5 mr-1" />{selectedGroupIds.size} groups</Badge>}
            {selectedDeptNames.size > 0 && <Badge variant="outline" className="text-[10px]"><Building2 className="w-2.5 h-2.5 mr-1" />{selectedDeptNames.size} depts</Badge>}
            <button className="text-[10px] text-muted-foreground underline hover:text-primary" onClick={() => { setSelectedUserIds(new Set()); setSelectedGroupIds(new Set()); setSelectedDeptNames(new Set()); }}>Clear all</button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0 space-y-1.5" style={{ maxHeight: "340px" }}>
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : tab === "people" ? (
            filteredUsers.length === 0 ? (
              <p className="text-center py-8 text-sm text-muted-foreground">No team members found</p>
            ) : filteredUsers.map(u => {
              const selected = selectedUserIds.has(u.id);
              return (
                <button
                  key={u.id}
                  onClick={() => toggleUser(u.id)}
                  className={`w-full flex items-center gap-3 p-2.5 rounded-lg border text-left transition-all ${
                    selected ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "hover:bg-muted/50"
                  }`}
                  data-testid={`assign-user-${u.id}`}
                >
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${selected ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}>
                    {u.full_name?.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{u.full_name}</p>
                    <p className="text-[10px] text-muted-foreground">{u.email} · {u.department || "No dept"} · {u.role}</p>
                  </div>
                  {selected && <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />}
                </button>
              );
            })
          ) : tab === "groups" ? (
            filteredGroups.length === 0 ? (
              <p className="text-center py-8 text-sm text-muted-foreground">No groups found</p>
            ) : filteredGroups.map(g => {
              const selected = selectedGroupIds.has(g.id);
              return (
                <button
                  key={g.id}
                  onClick={() => toggleGroup(g.id)}
                  className={`w-full flex items-center gap-3 p-2.5 rounded-lg border text-left transition-all ${
                    selected ? "border-violet-400 bg-violet-50/50 ring-1 ring-violet-200" : "hover:bg-muted/50"
                  }`}
                  data-testid={`assign-group-${g.id}`}
                >
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${selected ? "bg-violet-500 text-white" : "bg-violet-50 text-violet-400"}`}>
                    <FolderOpen className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{g.name}</p>
                    <p className="text-[10px] text-muted-foreground">{g.member_count || g.member_ids?.length || 0} members{g.description ? ` · ${g.description}` : ""}</p>
                  </div>
                  {selected && <CheckCircle2 className="w-4 h-4 text-violet-500 shrink-0" />}
                </button>
              );
            })
          ) : (
            filteredDepts.length === 0 ? (
              <p className="text-center py-8 text-sm text-muted-foreground">No departments found</p>
            ) : filteredDepts.map(d => {
              const selected = selectedDeptNames.has(d.name);
              return (
                <button
                  key={d.name}
                  onClick={() => toggleDept(d.name)}
                  className={`w-full flex items-center gap-3 p-2.5 rounded-lg border text-left transition-all ${
                    selected ? "border-emerald-400 bg-emerald-50/50 ring-1 ring-emerald-200" : "hover:bg-muted/50"
                  }`}
                  data-testid={`assign-dept-${d.name}`}
                >
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${selected ? "bg-emerald-500 text-white" : "bg-emerald-50 text-emerald-400"}`}>
                    <Building2 className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{d.name}</p>
                    <p className="text-[10px] text-muted-foreground">{d.member_count || 0} members{d.head ? ` · Head: ${d.head}` : ""}</p>
                  </div>
                  {selected && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
                </button>
              );
            })
          )}
        </div>

        <DialogFooter className="border-t pt-3">
          <div className="flex-1 text-xs text-muted-foreground">
            {totalSelected > 0 && `${resolveUserIds().length} total users will be assigned`}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleAssign}
            disabled={totalSelected === 0 || assigning}
            data-testid="confirm-assign-btn"
          >
            {assigning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
            Assign ({resolveUserIds().length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignmentModal;
